import React, { useState, useEffect, useMemo } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SendIcon from '@mui/icons-material/Send';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import BlockIcon from '@mui/icons-material/Block';
import dayjs from 'dayjs';
import api from '../../services/api';
import { useWorkspace } from '../../utils/WorkspaceContext';
import { COMPACT_DATE_TIME_FORMAT } from '../../utils/timeFormatter';
import { dateParams } from './NoteBasedTimeLog';
import CompactDateTimePicker from './CompactDateTimePicker';

/**
 * @typedef {Object} TrackerEntry
 * @property {number} id
 * @property {string} work_date - YYYY-MM-DD (IST)
 * @property {string} window_start
 * @property {string} window_end
 * @property {string} start_time - First activity on the action item
 * @property {number|null} action_item - Null when the item was deleted
 * @property {string|null} action_item_name
 * @property {boolean|null} action_item_archived
 * @property {string|null} task_name
 * @property {number|null} workspace
 * @property {number} minutes - Multiple of 5
 * @property {string} summary - Written by the tracker's LLM
 * @property {{kind: string, label: string, minutes: number}[]} evidence
 * @property {number} confidence - Tracker LLM confidence, 0-100
 * @property {'pending'|'approved'|'flagged'|'submitted'|'dismissed'} status
 * @property {string} ai_verdict
 * @property {boolean} user_edited
 * @property {boolean} overridden
 */

const VIEW_STATUSES = {
  review: 'pending,approved,flagged',
  dismissed: 'dismissed',
  submitted: 'submitted',
  all: '',
};

const STATUS_CHIP = {
  pending: { label: 'Not verified', color: 'default' },
  approved: { label: 'Approved', color: 'success' },
  flagged: { label: 'Flagged', color: 'warning' },
  submitted: { label: 'Submitted', color: 'info' },
  dismissed: { label: 'Dismissed', color: 'default' },
};

const OPEN_STATUSES = ['pending', 'approved', 'flagged'];

/**
 * "1h 35m" / "40m".
 * @param {number} minutes
 * @returns {string}
 */
export const formatMinutes = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (!hours) return `${rest}m`;
  return rest ? `${hours}h ${rest}m` : `${hours}h`;
};

/**
 * Groups entries by work date (newest first), then by action item. The same
 * item can have several entries a day, one per submission window.
 * @param {TrackerEntry[]} entries
 * @returns {{date: string, items: {key: string, entries: TrackerEntry[], minutes: number}[]}[]}
 */
export const groupEntries = (entries) => {
  const byDate = new Map();
  entries.forEach((entry) => {
    if (!byDate.has(entry.work_date)) byDate.set(entry.work_date, new Map());
    const items = byDate.get(entry.work_date);
    // Entries without an action item stay on their own so each can be reassigned.
    const key = entry.action_item ? `item-${entry.action_item}` : `entry-${entry.id}`;
    if (!items.has(key)) items.set(key, []);
    items.get(key).push(entry);
  });
  return [...byDate.entries()]
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([date, items]) => ({
      date,
      items: [...items.entries()].map(([key, group]) => ({
        key,
        entries: [...group].sort((a, b) => a.start_time.localeCompare(b.start_time)),
        minutes: group.reduce((sum, e) => sum + e.minutes, 0),
      })),
    }));
};

/**
 * Counts per-id results into one line: "Submitted 2. Skipped 1: not verified."
 * @param {{status: string, reason?: string}[]} results
 * @param {string} doneLabel
 * @param {string[]} doneStatuses - Result statuses that count as done
 * @returns {string}
 */
const summarizeResults = (results, doneLabel, doneStatuses) => {
  const done = results.filter((r) => doneStatuses.includes(r.status)).length;
  const reasons = {};
  results
    .filter((r) => !doneStatuses.includes(r.status))
    .forEach((r) => {
      const reason = r.reason || r.status;
      reasons[reason] = (reasons[reason] || 0) + 1;
    });
  const skipped = Object.entries(reasons).map(([reason, count]) => `${count} ${reason}`);
  return [`${doneLabel} ${done}.`, skipped.length ? `Not done: ${skipped.join(', ')}.` : '']
    .filter(Boolean)
    .join(' ');
};

/**
 * Edits one entry: action item, start time, minutes and summary. Saving sends
 * the entry back to "Not verified".
 * @param {Object} props
 * @param {TrackerEntry} props.entry
 * @param {number} props.workspaceId - Where to look for action items
 * @param {Function} props.onClose
 * @param {Function} props.onSaved - With the updated entry
 */
const TrackerEntryDialog = ({ entry, workspaceId, onClose, onSaved }) => {
  const [options, setOptions] = useState([]);
  const [actionItem, setActionItem] = useState(
    entry.action_item
      ? { id: entry.action_item, description: entry.action_item_name, task_name: entry.task_name }
      : null
  );
  const [startTime, setStartTime] = useState(entry.start_time);
  const [minutes, setMinutes] = useState(String(entry.minutes));
  const [summary, setSummary] = useState(entry.summary);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!workspaceId) return;
    api
      .get('/grouped-action-items/', { params: { workspace_id: workspaceId } })
      .then((response) =>
        setOptions(
          (response.data.payload || []).flatMap((task) =>
            (task.action_items || []).map((item) => ({
              id: item.id,
              description: item.description,
              task_name: task.task_name,
            }))
          )
        )
      )
      .catch((err) => console.error('Error fetching action items:', err));
  }, [workspaceId]);

  const handleSave = async () => {
    const updates = {};
    if (actionItem?.id !== entry.action_item) updates.action_item = actionItem?.id ?? null;
    if (!dayjs(startTime).isSame(dayjs(entry.start_time))) updates.start_time = startTime;
    if (Number(minutes) !== entry.minutes) updates.minutes = Number(minutes);
    if (summary.trim() !== entry.summary) updates.summary = summary.trim();
    if (!Object.keys(updates).length) {
      onClose();
      return;
    }
    setSaving(true);
    try {
      const response = await api.patch(`/tracker-logs/${entry.id}/`, updates);
      onSaved(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not save the entry.');
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit tracker entry · {dayjs(entry.work_date).format('ddd, MMM D')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <Autocomplete
            size="small"
            options={options}
            groupBy={(option) => option.task_name || ''}
            getOptionLabel={(option) => option.description || ''}
            isOptionEqualToValue={(option, selected) => option.id === selected.id}
            value={actionItem}
            onChange={(_, option) => setActionItem(option)}
            renderInput={(params) => <TextField {...params} label="Action item" />}
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ flex: 2 }}>
              <CompactDateTimePicker value={startTime} onChange={setStartTime} />
            </Box>
            <TextField
              label="Minutes"
              type="number"
              size="small"
              value={minutes}
              onChange={(e) => setMinutes(e.target.value)}
              inputProps={{ min: 5, step: 5 }}
              helperText="Multiple of 5"
              sx={{ flex: 1 }}
            />
          </Box>
          <TextField
            label="What was done"
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            multiline
            minRows={3}
            fullWidth
          />
          <Typography variant="caption" color="text.secondary">
            Saving sends the entry back to "Not verified"; verify it again before submitting.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || !actionItem}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * One tracker entry: its window, time, summary, evidence and AI verdict.
 * @param {Object} props
 * @param {TrackerEntry} props.entry
 * @param {boolean} props.checked
 * @param {boolean} props.verifying
 * @param {Function} props.onToggle
 * @param {Function} props.onEdit
 * @param {Function} props.onDismiss
 */
const TrackerEntryRow = ({ entry, checked, verifying, onToggle, onEdit, onDismiss }) => {
  const chip = STATUS_CHIP[entry.status] || STATUS_CHIP.pending;
  const editable = entry.status !== 'submitted';
  const start = dayjs(entry.start_time);
  const evidence = (entry.evidence || [])
    .filter((e) => e && e.label)
    .map((e) => `${e.label}${e.minutes ? ` ${e.minutes}m` : ''}`)
    .join(' · ');

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1.5,
        alignItems: 'flex-start',
        py: 1,
        borderTop: 1,
        borderColor: 'divider',
        opacity: entry.status === 'dismissed' ? 0.6 : 1,
      }}
    >
      <Checkbox
        size="small"
        checked={checked}
        disabled={!OPEN_STATUSES.includes(entry.status)}
        onChange={() => onToggle(entry.id)}
        inputProps={{ 'aria-label': `Select entry ${entry.id}` }}
        sx={{ p: 0.5 }}
      />
      <Box sx={{ width: 110, flexShrink: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
          {formatMinutes(entry.minutes)}
        </Typography>
        <Tooltip
          title={`Window ${dayjs(entry.window_start).format(COMPACT_DATE_TIME_FORMAT)} - ${dayjs(
            entry.window_end
          ).format(COMPACT_DATE_TIME_FORMAT)}`}
        >
          <Typography variant="caption" color="text.secondary">
            from {start.format('HH:mm')}
          </Typography>
        </Tooltip>
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          {entry.summary || <em>No summary</em>}
        </Typography>
        {evidence && (
          <Typography variant="caption" color="text.secondary" component="div">
            {evidence}
          </Typography>
        )}
        {entry.ai_verdict && (
          <Typography
            variant="caption"
            component="div"
            color={entry.status === 'flagged' ? 'warning.main' : 'text.secondary'}
            sx={{ mt: 0.5 }}
          >
            AI: {entry.ai_verdict}
          </Typography>
        )}
      </Box>
      <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0, alignItems: 'center' }}>
        {entry.user_edited && <Chip label="Edited" size="small" variant="outlined" />}
        {entry.overridden && <Chip label="Overridden" size="small" variant="outlined" color="warning" />}
        {verifying ? (
          <CircularProgress size={18} aria-label="Verifying" />
        ) : (
          <Chip label={chip.label} size="small" color={chip.color} />
        )}
        <Tooltip title={editable ? 'Edit' : 'Submitted entries cannot be changed'}>
          <span>
            <IconButton size="small" aria-label="Edit entry" disabled={!editable} onClick={() => onEdit(entry)}>
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Dismiss: this is not work to log">
          <span>
            <IconButton
              size="small"
              aria-label="Dismiss entry"
              disabled={!OPEN_STATUSES.includes(entry.status)}
              onClick={() => onDismiss(entry)}
            >
              <BlockIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    </Box>
  );
};

/**
 * Tracker based time logging: the activity tracker's daily summaries, grouped
 * by date and action item. The user verifies them with AI, fixes or dismisses
 * what is wrong, and submits; only submitting creates main time logs.
 * @component
 */
const TrackerTimeLog = () => {
  const { currentWorkspace } = useWorkspace();
  const [entries, setEntries] = useState([]);
  const [view, setView] = useState('review');
  const [datePreset, setDatePreset] = useState('last_7');
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [verifyingIds, setVerifyingIds] = useState(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [confirmOverride, setConfirmOverride] = useState(false);
  const [editing, setEditing] = useState(null);
  const [message, setMessage] = useState(null); // { severity, text }

  const fetchEntries = async () => {
    try {
      const params = dateParams(datePreset, rangeStart, rangeEnd);
      if (VIEW_STATUSES[view]) params.status = VIEW_STATUSES[view];
      const response = await api.get('/tracker-logs/', { params });
      setEntries(response.data.payload || []);
    } catch (err) {
      console.error('Error fetching tracker entries:', err);
      setMessage({ severity: 'error', text: 'Could not load tracker entries.' });
    }
  };

  useEffect(() => {
    setSelected(new Set());
    fetchEntries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, datePreset, rangeStart, rangeEnd]);

  const groups = useMemo(() => groupEntries(entries), [entries]);
  const selectable = entries.filter((e) => OPEN_STATUSES.includes(e.status));
  const selectedEntries = selectable.filter((e) => selected.has(e.id));
  const toVerify = selectedEntries.filter((e) => e.action_item);
  const toSubmit = selectedEntries.filter((e) => ['approved', 'flagged'].includes(e.status) && !e.action_item_archived);
  const flaggedCount = toSubmit.filter((e) => e.status === 'flagged').length;
  const verifying = verifyingIds.size > 0;

  const replaceEntry = (updated) =>
    setEntries((prev) => prev.map((e) => (e.id === updated.id ? { ...e, ...updated } : e)));

  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleMany = (ids) =>
    setSelected((prev) => {
      const next = new Set(prev);
      const allOn = ids.every((id) => next.has(id));
      ids.forEach((id) => (allOn ? next.delete(id) : next.add(id)));
      return next;
    });

  // Verdicts stream in as NDJSON (same reader as the note link dialog).
  const handleVerify = async () => {
    const ids = toVerify.map((e) => e.id);
    setVerifyingIds(new Set(ids));
    setMessage(null);
    const results = [];
    try {
      const response = await fetch(`${api.defaults.baseURL}tracker-logs/verify/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
        body: JSON.stringify({ ids }),
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let pending = '';
      while (true) {
        const { done, value } = await reader.read();
        const text = done ? pending + decoder.decode() : pending + decoder.decode(value, { stream: true });
        const lines = text.split('\n');
        pending = done ? '' : lines.pop();
        for (const line of lines) {
          if (!line.trim()) continue;
          let event;
          try {
            event = JSON.parse(line);
          } catch (parseError) {
            console.error('Bad verify event:', line, parseError);
            continue;
          }
          if (event.type !== 'result') continue;
          results.push(event);
          if (['approved', 'flagged'].includes(event.status)) {
            replaceEntry({ id: event.id, status: event.status, ai_verdict: event.ai_verdict });
          }
          setVerifyingIds((prev) => {
            const next = new Set(prev);
            next.delete(event.id);
            return next;
          });
        }
        if (done) break;
      }
      setMessage({
        severity: results.some((r) => r.status === 'error') ? 'warning' : 'success',
        text: summarizeResults(results, 'Verified', ['approved', 'flagged']),
      });
    } catch (err) {
      console.error('Error verifying tracker entries:', err);
      setMessage({ severity: 'error', text: 'AI verification failed. Try again.' });
    } finally {
      setVerifyingIds(new Set());
    }
  };

  const submit = async (override) => {
    setConfirmOverride(false);
    setSubmitting(true);
    setMessage(null);
    try {
      const response = await api.post('/tracker-logs/submit/', {
        ids: toSubmit.map((e) => e.id),
        override,
      });
      const results = response.data.results || [];
      setMessage({
        severity: results.every((r) => r.status === 'submitted') ? 'success' : 'warning',
        text: summarizeResults(results, 'Submitted', ['submitted']),
      });
      setSelected(new Set());
      await fetchEntries();
    } catch (err) {
      console.error('Error submitting tracker entries:', err);
      setMessage({ severity: 'error', text: 'Could not submit the entries.' });
    } finally {
      setSubmitting(false);
    }
  };

  // Flagged entries go in only after the user confirms them.
  const handleSubmit = () => (flaggedCount ? setConfirmOverride(true) : submit(false));

  const handleDismiss = async (entry) => {
    try {
      const response = await api.patch(`/tracker-logs/${entry.id}/`, { status: 'dismissed' });
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(entry.id);
        return next;
      });
      if (view === 'review') setEntries((prev) => prev.filter((e) => e.id !== entry.id));
      else replaceEntry(response.data);
    } catch (err) {
      setMessage({ severity: 'error', text: err.response?.data?.detail || 'Could not dismiss the entry.' });
    }
  };

  const handleSaved = (updated) => {
    setEditing(null);
    // An edited dismissed entry is back up for review.
    if (view === 'dismissed' && updated.status !== 'dismissed') {
      setEntries((prev) => prev.filter((e) => e.id !== updated.id));
    } else {
      replaceEntry(updated);
    }
  };

  const allSelectableIds = selectable.map((e) => e.id);

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Checkbox
          size="small"
          checked={allSelectableIds.length > 0 && allSelectableIds.every((id) => selected.has(id))}
          indeterminate={selected.size > 0 && !allSelectableIds.every((id) => selected.has(id))}
          disabled={!allSelectableIds.length}
          onChange={() => toggleMany(allSelectableIds)}
          inputProps={{ 'aria-label': 'Select all entries' }}
        />
        <Button
          variant="outlined"
          startIcon={<AutoAwesomeIcon />}
          disabled={!toVerify.length || verifying || submitting}
          onClick={handleVerify}
        >
          Verify with AI{toVerify.length ? ` (${toVerify.length})` : ''}
        </Button>
        <Tooltip title={toSubmit.length ? '' : 'Verify entries before submitting them'}>
          <span>
            <Button
              variant="contained"
              startIcon={<SendIcon />}
              disabled={!toSubmit.length || verifying || submitting}
              onClick={handleSubmit}
            >
              Submit{toSubmit.length ? ` (${toSubmit.length})` : ''}
            </Button>
          </span>
        </Tooltip>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Show</InputLabel>
          <Select value={view} label="Show" onChange={(e) => setView(e.target.value)}>
            <MenuItem value="review">To review</MenuItem>
            <MenuItem value="dismissed">Dismissed</MenuItem>
            <MenuItem value="submitted">Submitted</MenuItem>
            <MenuItem value="all">All</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Date</InputLabel>
          <Select value={datePreset} label="Date" onChange={(e) => setDatePreset(e.target.value)}>
            <MenuItem value="today">Today</MenuItem>
            <MenuItem value="last_3">Last 3 days</MenuItem>
            <MenuItem value="last_7">Last 7 days</MenuItem>
            <MenuItem value="last_month">Last month</MenuItem>
            <MenuItem value="custom">Custom Range</MenuItem>
          </Select>
        </FormControl>
        {datePreset === 'custom' && (
          <>
            <TextField
              type="date"
              label="From"
              size="small"
              value={rangeStart}
              onChange={(e) => setRangeStart(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              type="date"
              label="To"
              size="small"
              value={rangeEnd}
              onChange={(e) => setRangeEnd(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </>
        )}
      </Box>

      {message && (
        <Alert severity={message.severity} onClose={() => setMessage(null)} sx={{ mb: 2 }}>
          {message.text}
        </Alert>
      )}

      {groups.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
          No tracker entries for this period. The tracker sends them every day at 18:00, or when you press Sync in
          the tracker app.
        </Typography>
      )}

      {groups.map(({ date, items }) => {
        const dayMinutes = items.reduce((sum, item) => sum + item.minutes, 0);
        return (
          <Box key={date} sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
              {dayjs(date).format('dddd, MMM D')}
              <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                {formatMinutes(dayMinutes)}
              </Typography>
            </Typography>
            {items.map(({ key, entries: itemEntries, minutes }) => {
              const first = itemEntries[0];
              const itemIds = itemEntries.filter((e) => OPEN_STATUSES.includes(e.status)).map((e) => e.id);
              return (
                <Paper key={key} variant="outlined" sx={{ px: 1.5, py: 1, mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Checkbox
                      size="small"
                      checked={itemIds.length > 0 && itemIds.every((id) => selected.has(id))}
                      disabled={!itemIds.length}
                      onChange={() => toggleMany(itemIds)}
                      inputProps={{ 'aria-label': `Select ${first.action_item_name || 'entry'}` }}
                      sx={{ p: 0.5 }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      {first.action_item ? (
                        <>
                          <Typography variant="body2" component="div" sx={{ fontWeight: 'bold' }}>
                            {first.action_item_name}
                            {first.action_item_archived && (
                              <Chip label="Archived" size="small" color="error" variant="outlined" sx={{ ml: 1 }} />
                            )}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {first.task_name}
                            {first.workspace && first.workspace !== currentWorkspace?.id && first.workspace_name
                              ? ` · ${first.workspace_name}`
                              : ''}
                          </Typography>
                        </>
                      ) : (
                        <Typography variant="body2" color="error">
                          Action item was deleted. Edit the entry to pick another one.
                        </Typography>
                      )}
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {formatMinutes(minutes)}
                    </Typography>
                  </Box>
                  {itemEntries.map((entry) => (
                    <TrackerEntryRow
                      key={entry.id}
                      entry={entry}
                      checked={selected.has(entry.id)}
                      verifying={verifyingIds.has(entry.id)}
                      onToggle={toggle}
                      onEdit={setEditing}
                      onDismiss={handleDismiss}
                    />
                  ))}
                </Paper>
              );
            })}
          </Box>
        );
      })}

      {editing && (
        <TrackerEntryDialog
          entry={editing}
          workspaceId={editing.workspace || currentWorkspace?.id}
          onClose={() => setEditing(null)}
          onSaved={handleSaved}
        />
      )}

      <Dialog open={confirmOverride} onClose={() => setConfirmOverride(false)}>
        <DialogTitle>Submit flagged entries?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            The AI flagged {flaggedCount} of the {toSubmit.length} selected entr{toSubmit.length === 1 ? 'y' : 'ies'}.
            Submitting them anyway logs their time as written and marks them as overridden.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOverride(false)}>Cancel</Button>
          <Button onClick={() => submit(false)} disabled={flaggedCount === toSubmit.length}>
            Submit approved only
          </Button>
          <Button variant="contained" color="warning" onClick={() => submit(true)}>
            Submit all
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TrackerTimeLog;
